/**
 * Checklist Actions Hook
 *
 * Wraps checklist mutations (complete, uncomplete, generateSummary,
 * generateInformation, generateExamples, findRelatedDocuments)
 * using useApiMutation for consistent error handling.
 */

import { api } from "convex/_generated/api";
import type { AuditId, Id } from "@/packages/types";
import { useApiMutation } from "@/packages/features-shared";
import { getCurrentLanguage } from "@/packages/utils";
import { useTranslation } from "react-i18next";

/* ------------------------------------------------------------------ */
/* Hook                                                                */
/* ------------------------------------------------------------------ */

export function useChecklistActions(auditId: AuditId) {
  const { t } = useTranslation("audits");

  const completeMutation = useApiMutation(
    api.checklist_mutations.completeChecklistItem,
    { name: "complete-checklist-item", silentSuccess: true },
  );
  const uncompleteMutation = useApiMutation(
    api.checklist_mutations.uncompleteChecklistItem,
    { name: "uncomplete-checklist-item", silentSuccess: true },
  );
  const generateSummaryMutation = useApiMutation(
    api.checklist_mutations.generateChecklistItemSummary,
    {
      name: "generate-checklist-summary",
      successMessage: t("checklist.mutations.summaryStarted"),
    },
  );
  const generateInformationMutation = useApiMutation(
    api.checklist_mutations.generateChecklistItemInformation,
    {
      name: "generate-checklist-information",
      successMessage: t("checklist.mutations.informationStarted"),
    },
  );
  const generateExamplesMutation = useApiMutation(
    api.checklist_mutations.generateChecklistItemExamples,
    {
      name: "generate-checklist-examples",
      successMessage: t("checklist.mutations.examplesStarted"),
    },
  );
  const findRelatedDocumentsMutation = useApiMutation(
    api.checklist_mutations.findRelatedDocumentsForChecklistItem,
    {
      name: "find-related-documents",
      successMessage: t("checklist.mutations.documentSearchStarted"),
    },
  );
  const updateDetailsMutation = useApiMutation(
    api.checklist_mutations.updateChecklistItemDetails,
    {
      name: "update-checklist-item-details",
      successMessage: t("checklist.mutations.itemUpdated"),
    },
  );
  const runFullPipelineMutation = useApiMutation(
    api.checklist_mutations.runFullPipeline,
    {
      name: "run-full-pipeline",
      successMessage: t("checklist.mutations.pipelineStarted"),
    },
  );

  return {
    complete: (checklistItemId: Id<"checklistItems">) =>
      completeMutation.mutateAsync({ checklistItemId }),
    uncomplete: (checklistItemId: Id<"checklistItems">) =>
      uncompleteMutation.mutateAsync({ checklistItemId }),
    generateSummary: (checklistItemId: Id<"checklistItems">) =>
      generateSummaryMutation.mutateAsync({
        checklistItemId,
        auditId: auditId,
        locale: getCurrentLanguage(),
      }),
    generateInformation: (
      checklistItemId: Id<"checklistItems">,
      customInstructions?: string,
    ) =>
      generateInformationMutation.mutateAsync({
        checklistItemId,
        auditId: auditId,
        customInstructions: customInstructions || undefined,
        locale: getCurrentLanguage(),
      }),
    generateExamples: (
      checklistItemId: Id<"checklistItems">,
      customInstructions?: string,
    ) =>
      generateExamplesMutation.mutateAsync({
        checklistItemId,
        auditId: auditId,
        customInstructions: customInstructions || undefined,
        locale: getCurrentLanguage(),
      }),
    findRelatedDocuments: (checklistItemId: Id<"checklistItems">) =>
      findRelatedDocumentsMutation.mutateAsync({ checklistItemId }),
    updateDetails: (
      checklistItemId: Id<"checklistItems">,
      description: string,
      notes: string,
    ) =>
      updateDetailsMutation.mutateAsync({
        checklistItemId,
        description,
        notes,
      }),
    runFullPipeline: (checklistItemId: Id<"checklistItems">) =>
      runFullPipelineMutation.mutateAsync({
        checklistItemId,
        auditId,
        locale: getCurrentLanguage(),
      }),
    isRunningFullPipeline: runFullPipelineMutation.isPending,
  };
}
